(() => {
  const SELECTOR = '[data-lg-reels]';

  class LGReels {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-lg-track]');
      this.cards = Array.from(root.querySelectorAll('[data-lg-reel-card]'));
      this.prevButton = root.querySelector('[data-lg-prev]');
      this.nextButton = root.querySelector('[data-lg-next]');
      this.currentOutput = root.querySelector('[data-lg-current]');
      this.status = root.querySelector('[data-lg-status]');

      this.layout = root.dataset.layout || 'classic';
      this.autoplay = root.dataset.autoplay === 'true';
      this.loopVideo = root.dataset.loopVideo === 'true';
      this.loopNavigation = root.dataset.loopNavigation === 'true';
      this.preloadNext = root.dataset.preloadNext === 'true';
      this.pauseOffscreen = root.dataset.pauseOffscreen === 'true';
      this.respectReducedMotion = root.dataset.respectReducedMotion === 'true';
      this.delay = Math.max(0, parseInt(root.dataset.loadDelay || '2500', 10));
      this.activationDistance = Math.max(0, parseInt(root.dataset.activationDistance || '600', 10));

      this.reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
      if (this.respectReducedMotion && this.reducedMotion) this.autoplay = false;

      this.activeIndex = 0;
      this.delayReady = false;
      this.nearViewport = false;
      this.sectionVisible = false;
      this.started = false;
      this.destroyed = false;
      this.sharedMutedState = true;
      this.scrollRaf = null;
      this.delayTimer = null;

      if (!this.track || !this.cards.length) return;

      this.bind();
      this.updatePositions();
      this.updateArrows();
      this.createObservers();

      this.delayTimer = window.setTimeout(() => {
        this.delayReady = true;
        this.maybeStart();
      }, this.delay);
    }

    bind() {
      this.onPrev = () => this.move(-1);
      this.onNext = () => this.move(1);
      this.onTrackScroll = () => {
        if (this.layout !== 'classic') return;
        if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
        this.scrollRaf = requestAnimationFrame(() => this.syncClassicActiveCard());
      };
      this.onCardClick = (event) => {
        const card = event.target.closest('[data-lg-reel-card]');
        if (!card || !this.root.contains(card)) return;
        if (event.target.closest('a, button')) return;
        const index = Number(card.dataset.reelIndex);
        if (!Number.isFinite(index)) return;

        if (index !== this.activeIndex) {
          this.setActive(index, true);
        } else {
          this.toggleActivePlayback();
        }
      };
      this.onCardKeydown = (event) => {
        if (!['Enter', ' '].includes(event.key)) return;
        if (event.target.closest('a, button')) return;
        const card = event.target.closest('[data-lg-reel-card]');
        if (!card) return;
        event.preventDefault();
        const index = Number(card.dataset.reelIndex);
        if (index !== this.activeIndex) {
          this.setActive(index, true);
        } else {
          this.toggleActivePlayback();
        }
      };
      this.onMute = (event) => {
        const button = event.target.closest('[data-lg-mute]');
        if (!button || !this.root.contains(button)) return;
        event.stopPropagation();
        const card = button.closest('[data-lg-reel-card]');
        const video = card?.querySelector('[data-lg-video]');
        if (!video) return;

        this.sharedMutedState = !video.muted;
        video.muted = this.sharedMutedState;
        this.syncMuteButtons();
      };
      this.onVisibilityChange = () => {
        if (document.hidden) {
          this.pauseAll();
        } else if (this.sectionVisible && this.started) {
          this.playActive();
        }
      };

      this.prevButton?.addEventListener('click', this.onPrev);
      this.nextButton?.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
      this.track.addEventListener('click', this.onCardClick);
      this.track.addEventListener('keydown', this.onCardKeydown);
      this.root.addEventListener('click', this.onMute);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    createObservers() {
      this.nearObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.nearViewport = true;
            this.maybeStart();
          }
        },
        {
          root: null,
          rootMargin: `${this.activationDistance}px 0px`,
          threshold: 0
        }
      );

      this.visibleObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.sectionVisible = Boolean(entry?.isIntersecting);

          if (!this.sectionVisible && this.pauseOffscreen) {
            this.pauseAll();
          } else if (this.sectionVisible && this.started) {
            this.playActive();
          }
        },
        {
          root: null,
          threshold: 0.06
        }
      );

      this.nearObserver.observe(this.root);
      this.visibleObserver.observe(this.root);
    }

    maybeStart() {
      if (this.destroyed || this.started || !this.delayReady || !this.nearViewport) return;
      this.started = true;
      this.root.classList.add('is-lg-started');
      this.loadAndMaybePlay(this.activeIndex, false);

      if (this.preloadNext && this.cards.length > 1) {
        const next = this.normalizeIndex(this.activeIndex + 1);
        if (next !== this.activeIndex) this.loadVideo(next, true);
      }
    }

    normalizeIndex(index) {
      const count = this.cards.length;
      if (!count) return 0;

      if (this.loopNavigation) {
        return ((index % count) + count) % count;
      }

      return Math.max(0, Math.min(index, count - 1));
    }

    move(delta) {
      if (this.cards.length < 2) return;
      const target = this.normalizeIndex(this.activeIndex + delta);
      if (target === this.activeIndex && !this.loopNavigation) return;
      this.setActive(target, true);
    }

    setActive(index, userInitiated = false) {
      const nextIndex = this.normalizeIndex(index);
      if (!Number.isFinite(nextIndex)) return;

      this.activeIndex = nextIndex;
      this.updatePositions();
      this.updateArrows();
      this.updateCounter();

      if (this.layout === 'classic' && userInitiated) {
        this.cards[nextIndex]?.scrollIntoView({
          behavior: this.reducedMotion ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }

      if (this.started) {
        this.loadAndMaybePlay(nextIndex, userInitiated);

        if (this.preloadNext && this.cards.length > 1) {
          const preloadIndex = this.normalizeIndex(nextIndex + 1);
          if (preloadIndex !== nextIndex) this.loadVideo(preloadIndex, true);
        }
      }

      if (this.status) {
        this.status.textContent = `Reel ${nextIndex + 1} of ${this.cards.length}`;
      }
    }

    updatePositions() {
      this.cards.forEach((card, index) => {
        card.classList.toggle('is-active', index === this.activeIndex);

        if (this.layout !== 'center') {
          card.dataset.pos = index === this.activeIndex ? 'active' : 'hidden';
          return;
        }

        card.dataset.pos = 'hidden';
      });

      if (this.layout !== 'center' || !this.cards.length) return;

      const active = this.activeIndex;
      this.cards[active].dataset.pos = 'active';

      if (this.cards.length === 2) {
        const other = active === 0 ? 1 : 0;
        this.cards[other].dataset.pos = 'next';
        return;
      }

      if (this.cards.length > 2) {
        const prev = this.normalizeIndex(active - 1);
        const next = this.normalizeIndex(active + 1);

        if (prev !== active) this.cards[prev].dataset.pos = 'prev';
        if (next !== active && next !== prev) this.cards[next].dataset.pos = 'next';
      }
    }

    updateArrows() {
      if (!this.prevButton && !this.nextButton) return;

      if (this.loopNavigation) {
        if (this.prevButton) this.prevButton.disabled = this.cards.length < 2;
        if (this.nextButton) this.nextButton.disabled = this.cards.length < 2;
        return;
      }

      if (this.prevButton) this.prevButton.disabled = this.activeIndex <= 0;
      if (this.nextButton) this.nextButton.disabled = this.activeIndex >= this.cards.length - 1;
    }

    updateCounter() {
      if (this.currentOutput) this.currentOutput.textContent = String(this.activeIndex + 1);
    }

    syncClassicActiveCard() {
      if (this.layout !== 'classic' || !this.cards.length) return;

      const trackRect = this.track.getBoundingClientRect();
      const center = trackRect.left + trackRect.width / 2;
      let bestIndex = this.activeIndex;
      let bestDistance = Infinity;

      this.cards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        const cardCenter = rect.left + rect.width / 2;
        const distance = Math.abs(cardCenter - center);

        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });

      if (bestIndex !== this.activeIndex) {
        this.setActive(bestIndex, false);
      }
    }

    loadVideo(index, preloadOnly = false) {
      const card = this.cards[index];
      const video = card?.querySelector('[data-lg-video]');
      if (!video) return null;

      if (!video.dataset.lgLoaded) {
        const poster = video.dataset.poster;
        if (poster) video.poster = poster;

        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
          if (source.dataset.type) source.type = source.dataset.type;
        });

        video.preload = preloadOnly ? 'metadata' : 'auto';
        video.loop = this.loopVideo;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.dataset.lgLoaded = 'true';

        const markReady = () => card.classList.add('is-video-ready');
        if (video.readyState >= 2) {
          markReady();
        } else {
          video.addEventListener('loadeddata', markReady, { once: true });
        }

        video.addEventListener('playing', () => {
          card.classList.add('is-playing');
          card.classList.add('is-video-ready');
        });

        video.addEventListener('pause', () => {
          card.classList.remove('is-playing');
        });

        video.addEventListener('ended', () => {
          if (!this.loopVideo) card.classList.remove('is-playing');
        });

        video.load();
      } else if (!preloadOnly) {
        video.preload = 'auto';
      }

      return video;
    }

    loadAndMaybePlay(index) {
      this.pauseAll(index);
      const video = this.loadVideo(index, false);
      if (!video) return;

      video.muted = this.sharedMutedState;
      this.syncMuteButtons();

      if (this.autoplay && this.sectionVisible && !document.hidden) {
        const promise = video.play();
        if (promise?.catch) promise.catch(() => {});
      }
    }

    playActive() {
      const card = this.cards[this.activeIndex];
      const video = card?.querySelector('[data-lg-video]');
      if (!video || !video.dataset.lgLoaded || !this.autoplay) return;

      video.muted = this.sharedMutedState;
      const promise = video.play();
      if (promise?.catch) promise.catch(() => {});
    }

    pauseAll(exceptIndex = -1) {
      this.cards.forEach((card, index) => {
        if (index === exceptIndex) return;
        const video = card.querySelector('[data-lg-video]');
        if (video && !video.paused) video.pause();
        card.classList.remove('is-playing');
      });
    }

    toggleActivePlayback() {
      if (!this.started) {
        this.nearViewport = true;
        this.delayReady = true;
        this.maybeStart();
      }

      const card = this.cards[this.activeIndex];
      const video = this.loadVideo(this.activeIndex, false);
      if (!card || !video) return;

      if (video.paused) {
        video.muted = this.sharedMutedState;
        const promise = video.play();
        if (promise?.catch) promise.catch(() => {});
      } else {
        video.pause();
      }
    }

    syncMuteButtons() {
      this.cards.forEach((card) => {
        const button = card.querySelector('[data-lg-mute]');
        const video = card.querySelector('[data-lg-video]');
        if (!button || !video) return;

        button.classList.toggle('is-unmuted', !video.muted);
        button.setAttribute('aria-label', video.muted ? 'Unmute reel' : 'Mute reel');
      });
    }

    selectBlock(blockElement) {
      const card = blockElement?.closest?.('[data-lg-reel-card]');
      if (!card || !this.root.contains(card)) return;
      const index = Number(card.dataset.reelIndex);
      if (!Number.isFinite(index)) return;

      this.nearViewport = true;
      this.delayReady = true;
      this.maybeStart();
      this.setActive(index, true);
    }

    destroy() {
      this.destroyed = true;
      if (this.delayTimer) clearTimeout(this.delayTimer);
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);

      this.nearObserver?.disconnect();
      this.visibleObserver?.disconnect();

      this.prevButton?.removeEventListener('click', this.onPrev);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onTrackScroll);
      this.track?.removeEventListener('click', this.onCardClick);
      this.track?.removeEventListener('keydown', this.onCardKeydown);
      this.root?.removeEventListener('click', this.onMute);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);

      this.pauseAll();
      this.root.__lgReelsInstance = null;
    }
  }

  function initRoot(root) {
    if (!root || root.__lgReelsInstance) return root?.__lgReelsInstance;
    const instance = new LGReels(root);
    root.__lgReelsInstance = instance;
    return instance;
  }

  function initAll(scope = document) {
    scope.querySelectorAll?.(SELECTOR).forEach(initRoot);
    if (scope.matches?.(SELECTOR)) initRoot(scope);
  }

  if (!window.LGReels) {
    window.LGReels = { initAll, initRoot };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
    } else {
      initAll(document);
    }

    document.addEventListener('shopify:section:load', (event) => {
      initAll(event.target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const root = event.target.matches?.(SELECTOR)
        ? event.target
        : event.target.querySelector?.(SELECTOR);
      root?.__lgReelsInstance?.destroy?.();
    });

    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;
      const root = block.closest?.(SELECTOR);
      root?.__lgReelsInstance?.selectBlock?.(block);
    });
  } else {
    window.LGReels.initAll?.(document);
  }
})();
